import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Button, Chip, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import DoneOutlined from '@mui/icons-material/DoneOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import HandshakeOutlined from '@mui/icons-material/HandshakeOutlined';
import Add from '@mui/icons-material/Add';
import ArrowForward from '@mui/icons-material/ArrowForward';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import NotificationsNone from '@mui/icons-material/NotificationsNone';
import RequestQuoteOutlined from '@mui/icons-material/RequestQuoteOutlined';
import { can, orderPhase } from '@zilian/contracts';
import { api } from '../api';
import OrderFeedTable from '../OrderFeedTable';
import { amount, Empty, Heading, Props } from '../ui';

export function Dashboard(p: Props) {
  // Only the sides and shortcuts the member's roles open (see contracts/permissions.ts).
  const roles: string[] = p.current?.roles ?? [];
  const sides = (['BUY', 'SELL'] as const).filter((side) =>
    can(roles, side === 'BUY' ? 'purchase' : 'sales'),
  );
  // Sales is the everyday order to start; purchasing-only members start a purchase.
  const startSide = can(roles, 'sales') ? 'SELL' : can(roles, 'purchase') ? 'BUY' : null;
  const shortcuts = [
    can(roles, 'purchase') && (
      <Button key="buy" variant="outlined" fullWidth onClick={() => p.newOrder('BUY')}>
        向供应商要货
      </Button>
    ),
    can(roles, 'inventory') && (
      <Button key="stock" variant="outlined" fullWidth component={Link} to="/inventory">
        查询库存
      </Button>
    ),
    can(roles, 'products') && (
      <Button key="items" variant="outlined" fullWidth component={Link} to="/products">
        维护物料和产品
      </Button>
    ),
  ].filter(Boolean);
  const recent = (side: 'BUY' | 'SELL') => (
    <Paper key={side}>
      <div className="panel-title">
        <Typography sx={{ fontSize: 15, fontWeight: 650 }}>
          最近{side === 'BUY' ? '采购' : '销售'}
        </Typography>
        <Button
          component={Link}
          to={side === 'BUY' ? '/purchases' : '/sales'}
          size="small"
          endIcon={<ArrowForward fontSize="small" />}
        >
          {side === 'BUY' ? '采购管理' : '销售管理'}
        </Button>
      </div>
      <OrderFeedTable
        orders={p.feed.filter((row: any) => row.side === side).slice(0, 5)}
        postLocal={p.postLocal}
        quickStep={p.quickStep}
        openLocal={p.openLocal}
        side={side}
        compact
      />
    </Paper>
  );
  return (
    <>
      <Heading
        title="工作台"
        subtitle="关注每笔订单，连接每次交付。"
        action={
          startSide && (
            <Button variant="contained" startIcon={<Add />} onClick={() => p.newOrder(startSide)}>
              新建{startSide === 'SELL' ? '销售' : '采购'}订单
            </Button>
          )
        }
      />
      <div className="metric-grid">
        {[
          [
            '待确认订单',
            p.bootstrap.stats.pending,
            '需要双方确认约定',
            ReceiptLongOutlined,
            '#eaf2ee',
          ],
          [
            '履约中订单',
            p.bootstrap.stats.active +
              p.feed.filter((row: any) => row.source === 'LOCAL' && orderPhase(row) === 'delivery')
                .length,
            '跟进发货与实际验收',
            LocalShippingOutlined,
            '#edf2f9',
          ],
          [
            '待对账 / 收付款',
            p.feed.filter((row: any) => orderPhase(row) === 'money').length,
            '已交付，还没对账或没收付完',
            RequestQuoteOutlined,
            '#eef3f8',
          ],
          [
            '待处理差异',
            p.bootstrap.stats.exceptions,
            '少货、破损及补发协商',
            NotificationsNone,
            '#fbf2e6',
          ],
          [
            '合格可用库存',
            amount(p.bootstrap.stats.available),
            '按基础单位汇总，仅作概览',
            Inventory2Outlined,
            '#f2eff8',
          ],
        ].map(([label, value, hint, Icon, bg]: any) => (
          <div className="metric" key={label}>
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                color="text.secondary"
                sx={{
                  fontSize: 12,
                }}
              >
                {label}
              </Typography>
              <Box
                sx={{
                  p: 1,
                  bgcolor: bg,
                  borderRadius: 1,
                  color: '#6f8d85',
                  display: 'flex',
                }}
              >
                <Icon sx={{ fontSize: 18 }} />
              </Box>
            </Stack>
            <div className="metric-number">{value}</div>
            <Typography
              color="text.secondary"
              sx={{
                fontSize: 10,
              }}
            >
              {hint}
            </Typography>
          </div>
        ))}
      </div>
      {/* Explicit rows: 最近采购 | 协同待办, then 最近销售 | 快捷操作, so the two columns share
          top and bottom edges; the note spans both columns underneath. */}
      <div className="dashboard-grid">
        {sides[0] && recent(sides[0])}
        <TaskPanel tasks={p.bootstrap.tasks} safe={p.safe} openLocal={p.openLocal} />
        {sides[1] && recent(sides[1])}
        {shortcuts.length > 0 && (
          <Paper sx={{ p: 2.5, alignSelf: 'start' }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 650,
                mb: 1.5,
              }}
            >
              快捷操作
            </Typography>
            <Stack spacing={1}>{shortcuts}</Stack>
          </Paper>
        )}
        <Paper
          sx={{
            p: 3,
            gridColumn: '1 / -1',
            display: 'flex',
            gap: 3,
            alignItems: 'center',
            bgcolor: '#edf5f1',
            borderColor: '#dcebe3',
          }}
        >
          <Box
            sx={{
              color: 'primary.main',
            }}
          >
            <HandshakeOutlined sx={{ fontSize: 32 }} />
          </Box>
          <Box
            sx={{
              flex: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 650,
              }}
            >
              企业独立管理，交易彼此连接
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                fontSize: 12,
                mt: 1,
              }}
            >
              同一笔交易连接双方订单；发货与收货分别确认，各自库存独立记账。
            </Typography>
          </Box>
          <Button component={Link} to="/partners" size="small">
            管理合作关系
          </Button>
        </Paper>
      </div>
    </>
  );
}

const PREVIEW = 6;

/** "刚刚 / 12 分钟前 / 14:05 / 昨天 / 10月23日" — enough to tell what is fresh. */
function ago(value: string) {
  const d = new Date(value);
  const minutes = (Date.now() - d.getTime()) / 60000;
  if (minutes < 1) return '刚刚';
  if (minutes < 60) return `${Math.floor(minutes)} 分钟前`;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (d >= today) return d.toTimeString().slice(0, 5);
  if (d.getTime() >= today.getTime() - 86400000) return '昨天';
  return `${d.getMonth() + 1}月${d.getDate()}日`;
}

/**
 * Partner updates, one row per order: the newest event is the headline and older ones fold
 * into a count, so a busy order does not push everything else off the panel.
 */
function TaskPanel({
  tasks,
  safe,
  openLocal,
}: {
  tasks: any[];
  safe: (run: () => Promise<unknown>) => void;
  openLocal: (id: string) => void;
}) {
  const navigate = useNavigate();
  const [all, setAll] = useState(false);
  const groups: {
    key: string;
    local: boolean;
    number: string;
    text: string;
    at: string;
    ids: string[];
  }[] = [];
  for (const task of tasks) {
    const title: string = task.title ?? '';
    // Order events are titled "<order number> <what happened>"; statement events have no number.
    // A to-do points at either a shared order or one of this enterprise's own records.
    const match = /^((?:CO|LS|LP)-\S+)\s+(.*)$/.exec(title);
    const key = task.trade_id ?? task.local_order_id;
    let group = groups.find((g) => g.key === key);
    if (!group) {
      group = {
        key,
        local: !task.trade_id,
        number: match?.[1] ?? '',
        text: match?.[2] ?? title,
        at: task.created_at,
        ids: [],
      };
      groups.push(group);
    }
    group.ids.push(task.id);
  }
  const shown = all ? groups : groups.slice(0, PREVIEW);
  return (
    // Takes the height of the 最近采购 card beside it (height 0 + min-height 100% keeps it from
    // stretching that row); the list scrolls inside, the toggle stays at the bottom.
    <Paper
      sx={{
        height: { lg: 0 },
        minHeight: { lg: 'max(100%, 360px)' },
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div className="panel-title">
        <Typography sx={{ fontSize: 15, fontWeight: 650 }}>协同待办</Typography>
        {tasks.length > 0 && <Chip size="small" label={`${groups.length} 单`} />}
      </div>
      {groups.length ? (
        <>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {shown.map((g) => (
              <Box
                key={g.key}
                onClick={() => (g.local ? openLocal(g.key) : navigate(`/orders/${g.key}`))}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  px: 2.5,
                  py: 1.75,
                  borderBottom: '1px solid #edf1f2',
                  // The toggle below draws the divider; two borders would read as a double line.
                  '&:last-of-type': { borderBottom: 0 },
                  cursor: 'pointer',
                  '&:hover': { bgcolor: '#f7f9f9' },
                  '&:hover .task-done': { opacity: 1 },
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    mt: '7px',
                    flexShrink: 0,
                    borderRadius: '50%',
                    bgcolor: '#c98a2e',
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      lineHeight: 1.5,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {g.text}
                  </Typography>
                  <Typography color="text.secondary" noWrap sx={{ fontSize: 11.5, mt: 0.25 }}>
                    {[g.number, ago(g.at), g.ids.length > 1 && `另有 ${g.ids.length - 1} 条更新`]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                </Box>
                <Tooltip title="标记已处理">
                  <IconButton
                    className="task-done"
                    size="small"
                    aria-label="标记已处理"
                    onClick={(e) => {
                      e.stopPropagation();
                      safe(async () => {
                        for (const id of g.ids) await api(`/tasks/${id}/done`, {});
                      });
                    }}
                    sx={{
                      mt: -0.5,
                      mr: -1,
                      opacity: { xs: 1, md: 0.45 },
                      transition: 'opacity .15s',
                    }}
                  >
                    <DoneOutlined sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
          {groups.length > PREVIEW && (
            <Button
              fullWidth
              onClick={() => setAll(!all)}
              sx={{ py: 1.25, borderRadius: 0, flexShrink: 0, borderTop: '1px solid #edf1f2' }}
            >
              {all ? '收起' : `查看全部 ${groups.length} 单`}
            </Button>
          )}
        </>
      ) : (
        <Empty>暂无待处理事项</Empty>
      )}
    </Paper>
  );
}
